"use client";

import React from "react";
import { AlertCircle, BadgeDollarSign, BriefcaseBusiness, CircleCheck, ClipboardCheck, FileText, Mail, MapPin, NotebookTabs, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const focusAreas = [{
  title: "Financial Statement Preparation",
  description: "Prepare managment focused financial statements, working papers, reconciliations, lead sheets, and support schedules."
}, {
  title: "Audit File Development",
  description: "Organize audit-ready files, respond to audit requests, and support files through to completion."
}, {
  title: "Delayed Audit Recovery",
  description: "Help reconstruct and organize financial information across multiple fiscal years where reporting is delayed, incomplete, or difficult to follow."
}, {
  title: "Team Lead Coordination",
  description: "Serve as a team lead on assigned files by helping coordinate work, maintain momentum, and track timelines and deliverables."
}, {
  title: "Client and Auditor Communication",
  description: "Work with First Nation administration, RiverBend staff, auditors, and funders to gather information and resolve outstanding items."
}, {
  title: "Process Improvement",
  description: "Identify recurring file issues and support practical improvements to file organization, workflow, reporting, and future audit preparation."
}];
const successProfile = ["Technically strong and detail-oriented.", "Comfortable taking the lead on assigned files.", "Able to work independently and manage shifting priorities.", "Persistent and professional in follow-up.", "Comfortable working through incomplete records and historical issues.", "Respectful and culturally aware when working with First Nation clients and leadership."];
const requiredExperience = ["CPA designation, or strong demonstrated equivalent senior-level experience.", "Minimum 5 years of relevant experience in public accounting, financial reporting, year-end file preparation, audit support, or similar senior accounting work.", "Strong experience preparing financial statements, working papers, reconciliations, and audit support files.", "Strong understanding of how transactions flow through the general ledger into financial statements.", "Experience working with auditors and managing audit follow-up.", "Strong Excel skills and strong written and verbal communication skills.", "Ability to manage changing priorities, work independently, and help lead work within a collaborative environment."];
const strongAssets = ["Experience working with First Nation organizations, Indigenous communities, not-for-profit organizations, or public sector entities.", "Experience bringing delayed year-end files or audits up to date.", "Experience working with government funding, contribution agreements, or program reporting.", "Experience in advisory, controllership, or client-facing accounting roles.", "Experience with Caseware, QuickBooks, or similar reporting and year-end tools.", "Interest in emerging technologies, including AI, Microsoft 365 workflows, Power BI, and practical reporting innovation."];
export default function ServicesAccordion() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="257452" id="job-details-part-1" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-14 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 text-secondary bg-secondary/10 border-transparent">
            Position Summary
          </Badge>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary-500 mb-6">
            Financial Reporting and Program Support Lead
          </h2>

          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            RiverBend is seeking a Financial Reporting and Program Support Lead
            to help First Nation communities strengthen financial reporting,
            complete delayed audits, and build stronger systems for the future.
            This is a senior client-facing role for someone who can combine
            technical accounting depth, file leadership, practical
            problem-solving, and respectful communication.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12 transition-all duration-700 delay-100 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-2 space-y-10 min-w-0">
            <div>
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-5 flex items-center gap-2">
                <FileText className="text-secondary size-6" />
                What this role focuses on
              </h3>

              <p className="text-muted-foreground font-default leading-relaxed mb-6">
                This role focuses on financial statement preparation, audit file
                development, delayed audit recovery, and audit coordination. The
                successful candidate will help organize complex financial
                information, prepare detailed support, coordinate outstanding
                items, and work with RiverBend staff, First Nation
                administration, auditors, and funders to move important files to
                completion.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {focusAreas.map((item, i) => <div key={i} data-index={i} className="bg-primary-50/70 rounded-md p-5">
                    <div className="flex items-start gap-3 mb-2">
                      <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                      <h4 className="font-default font-bold text-primary-500">
                        {item.title}
                      </h4>
                    </div>

                    <p className="text-muted-foreground font-default text-sm leading-relaxed pl-8">
                      {item.description}
                    </p>
                  </div>)}
              </div>
            </div>

            <Separator className="bg-border" />

            <div>
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-5 flex items-center gap-2">
                <ClipboardCheck className="text-secondary size-6" />
                Success Profile
              </h3>

              <p className="text-muted-foreground font-default mb-6 leading-relaxed">
                This role is a strong fit for someone who is technically strong,
                organized, persistent, and comfortable leading files through
                incomplete information, historical issues, changing priorities,
                and multiple reporting environments.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {successProfile.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                    <span className="text-foreground font-medium font-default">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>

            <Separator className="bg-border" />

            <div>
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-5 flex items-center gap-2">
                <NotebookTabs className="text-secondary size-6" />
                Qualifications and Experience
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-border shadow-sm rounded-lg">
                  <CardContent className="p-6">
                    <h4 className="font-default font-bold text-primary-500 text-lg mb-4">
                      Required Experience
                    </h4>

                    <ul className="space-y-3">
                      {requiredExperience.map((item, i) => <li key={i} className="flex items-start gap-3">
                          <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                          <span className="text-muted-foreground font-default text-sm leading-relaxed">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border shadow-sm rounded-lg">
                  <CardContent className="p-6">
                    <h4 className="font-default font-bold text-primary-500 text-lg mb-4">
                      Strong Assets
                    </h4>

                    <ul className="space-y-3">
                      {strongAssets.map((item, i) => <li key={i} className="flex items-start gap-3">
                          <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                          <span className="text-muted-foreground font-default text-sm leading-relaxed">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6 bg-secondary/10 border border-secondary/20 rounded-md p-5 flex items-start gap-3">
                <UsersRound className="size-6 text-secondary shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-default font-bold text-primary-500 mb-2">
                    Senior judgment and file leadership matter
                  </h4>
                  <p className="text-muted-foreground font-default leading-relaxed">This role requires senior-level experience. It is intended for someone who can take ownership of assigned files, coordinate work with others, and help bring complex audit and financial reporting work to completion.</p>
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1 min-w-0">
            <Card className="border-border shadow-sm rounded-lg sticky top-8">
              <CardContent className="p-6 space-y-6">
                <div>
                  <p className="text-secondary font-default font-bold tracking-wider uppercase text-sm mb-3">
                    Role Snapshot
                  </p>

                  <h3 className="text-2xl font-default font-bold text-primary-500 mb-4">
                    Financial Reporting and Program Support Lead
                  </h3>

                  <p className="text-muted-foreground font-default leading-relaxed">
                    A senior full-time role focused on financial statement
                    preparation, audit-ready file development, delayed audit
                    recovery, file coordination, and practical client support.
                  </p>
                </div>

                <Separator className="bg-border" />

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="size-5 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-primary-500">
                        Location
                      </p>
                      <p className="text-sm text-muted-foreground">
                        1186 Roland St., Thunder Bay, Ontario
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <BriefcaseBusiness className="size-5 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-primary-500">
                        Employment Type
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Full-time, permanent
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <BadgeDollarSign className="size-5 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-primary-500">
                        Compensation
                      </p>
                      <p className="text-sm text-muted-foreground">
                        $100,000 to $110,000 per year
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <AlertCircle className="size-5 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-primary-500">
                        Work Model
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Primarily in-office in Thunder Bay. Some travel to
                        client communities may be required based on project
                        needs.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator className="bg-border" />

                <div>
                  <p className="font-semibold text-primary-500 mb-2">
                    Reports To
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Senior Projects Manager
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-primary-500 mb-2">
                    Application Deadline
                  </p>
                  <p className="text-sm text-muted-foreground">Open until filled</p>
                </div>

                <Button size="lg" className="w-full bg-secondary hover:bg-secondary/90 text-white font-default" asChild>
                  <a href="mailto:employment@riverbendcorp.ca?subject=Application%20-%20Financial%20Reporting%20and%20Program%20Support%20Lead">
                    <Mail className="mr-2 size-5" />
                    Apply by Email
                  </a>
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </section>;
}